<script setup lang="ts">
import { computed, useSlots } from 'vue';

import type { PhxPictureProps } from '@sg-core/src/View/Components/Atoms/Picture';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgPicture } from '../../Atoms/Picture';
import { PhxsgTag } from '../../Atoms/Tag';
import { PhxsgText } from '../../Atoms/Text';
import { PhxsgSlide, PhxsgSlider } from '../../Organisms/Slider';
import { PhxsgCard } from '../Card';

export interface PhxsgProductCardProps {
  images: PhxPictureProps[];
  title: string;
  description?: string;
  price?: string;
  href: string;
  tag?: string;
}

const { images, title, description, price, href, tag } = defineProps<PhxsgProductCardProps>();

const slots = useSlots();

const isSlider = computed(() => images.length > 1);
const hasWishlistButton = computed(() => !!slots['wishlist-button']);
const hasPrice = computed(() => !!price || !!slots.price);
</script>

<template>
  <PhxsgCard class="phxsg-product-card">
    <template #media>
      <PhxsgTag v-if="tag" class="phxsg-product-card__tag">{{ tag }}</PhxsgTag>

      <div class="phxsg-product-card__media">
        <div v-if="hasWishlistButton" class="phxsg-product-card__wishlist-button">
          <slot name="wishlist-button" />
        </div>

        <PhxsgSlider
          v-if="isSlider"
          wrapper-class="phxsg-product-card__slider-wrapper"
          class="phxsg-product-card__slider"
          :navigation="false"
          :pagination="false"
          :scrollbar="true"
        >
          <PhxsgSlide v-for="(image, index) in images" :key="`${title}-${index}`" class="phxsg-product-card__slide">
            <PhxsgPicture v-bind="image" type="contain" />
          </PhxsgSlide>
        </PhxsgSlider>

        <PhxsgPicture v-else v-bind="images[0]" type="contain" />
      </div>
    </template>

    <template #content>
      <div class="phxsg-product-card__content">
        <div class="phxsg-product-card__text-group">
          <PhxsgText use="h3" variant="body" size="medium" class="phxsg-product-card__title">
            <PhxsgButton use="a" :href="href" :aria-label="title" expand-target bare class="phxsg-product-card__link">
              {{ title }}
            </PhxsgButton>
          </PhxsgText>

          <PhxsgText v-if="description" variant="body" size="small" color="subtle" wrap="pretty">
            {{ description }}
          </PhxsgText>
        </div>

        <PhxsgText v-if="hasPrice" variant="specs" size="medium" color="subtle" class="phxsg-product-card__price">
          <slot name="price">
            {{ price }}
          </slot>
        </PhxsgText>
      </div>
    </template>
  </PhxsgCard>
</template>

<style src="./product-card.css" />
